import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ChartWidgetWrapper from '../ChartWidgetWrapper';
import BubbleChart from './BubbleChart';
import VariwideChart from './VariwideChart';
import { useFetchTableData } from '../../../customDashboards/hooks';
import { getMetricsFormat, getMetricValueFormat } from '../PivotChart/utils';
import { normalizeDimensionNames } from '../../../customDashboards/utils';
import { calculatedMetricValue } from '../../../utils/grids';
import { ACTION_TYPES } from '../ChartWidgetWrapper/consts';
import { THREE_AXIS_WIDGETS_TYPES } from './consts';
import { FORMAT_POINT_BY_WIDGET_TYPE } from './utils';
import TopXChartDataFilter from '../TopXChartDataFilter';
import DimensionsChartDataFilter from '../DimensionsChartDataFilter';
import { TOP_X_COUNT_MAX_VALUE, TOP_X_THRESHOLD } from '../PivotChart/consts';
import { getBlockedWidgetFilterNames } from '../../../customDashboards/universalFiltersConfig';
import { OPERATOR_TYPES } from '../../../utils/dashboardConsts';

const ThreeAxisChartsWidget = ({
    dispatchWidgetVersions,
    dispatchWidgetsLoading,
    forceRefreshCounter,
    dashboardId,
    widget,
    globalFilters = [],
    startDate,
    endDate,
    startDate2 = null,
    endDate2 = null,
    compare,
    height,
    width,
    updatingGraphQL = false,
    allowedActions = [],
    displayUnenriched = false,
    apiFields = null,
    readOnly = false,
}) => {
    const NO_VALUE = 'N/A';
    const reportType = 'reports';

    const [isDataParsed, setIsDataParsed] = useState(false);
    const [xAxisOptions, setXAxisOptions] = useState({});
    const [yAxisOptions, setYAxisOptions] = useState({});
    const [tooltipFormat, setTooltipFormat] = useState({});
    const [dimensionsWithValues, setDimensionsWithValues] = useState([]);
    const [selectedDimension, setSelectedDimension] = useState();
    const [series, setSeries] = useState([]);
    const [loading, tableData, error] = useFetchTableData(
        dashboardId,
        widget.gridParams.i,
        widget.query,
        globalFilters,
        startDate,
        endDate,
        startDate2,
        endDate2,
        reportType,
        dispatchWidgetVersions,
        dispatchWidgetsLoading,
        compare,
        widget.dataType,
        displayUnenriched,
        updatingGraphQL,
        forceRefreshCounter,
        undefined,
        widget.type,
        widget.options
    );

    const { rowData = [] } = tableData || {};
    const withTopX = widget?.metricForTopX && series.length >= TOP_X_THRESHOLD;

    const reportColumns = useMemo(() => {
        return tableData?.columnDefs.map(x => ({
            name: x.field,
            fieldType: x.type,
            displayName: x.headerName,
            displayFormat: x.context?.displayFormat,
        }));
    }, [tableData]);

    const [topXCount, setTopXCount] = useState(TOP_X_COUNT_MAX_VALUE);

    useEffect(() => {
        if (series.length) {
            setTopXCount(Math.min(series.length, TOP_X_COUNT_MAX_VALUE));
        }
    }, [series]);

    const getMetricName = axisMetric =>
        normalizeDimensionNames(axisMetric, widget.dataType).filter(metric =>
            reportColumns.find(column => column.name === metric)
        );

    const getMetricValue = (key, row) => {
        const value = typeof row[key] === 'object' ? calculatedMetricValue(row[key]) : row[key];
        const colObject = reportColumns.find(x => x.name === key);

        if (colObject?.displayFormat?.type === 'percentage') {
            return value * 100;
        }
        return value;
    };

    const getColumnDisplayName = (columns, dimension) => columns.find(col => col.name === dimension)?.displayName;

    const settings = useMemo(() => {
        if (!reportColumns) {
            return null;
        }

        const { rows = [], columns = [], zAxis = [] } = widget.options;
        const { dimensions = [] } = widget.query;

        const [xMetricName] = getMetricName(columns);
        const [yMetricName] = getMetricName(rows);
        const [zMetricName] = getMetricName(zAxis);

        const metrics = getMetricsFormat(reportColumns);
        const [initialDimension] = getMetricName(dimensions);
        setSelectedDimension(prevState => ({
            ...prevState,
            name: initialDimension,
            displayName: getColumnDisplayName(reportColumns, initialDimension),
        }));
        return { metrics, xMetricName, yMetricName, zMetricName };
    }, [reportColumns]);

    const setAxisAndTooltipSettings = (metrics, xMetricName, yMetricName, zMetricName) => {
        metrics.forEach(metric => {
            const { valuePrefix, valueSuffix, valueDecimals, formatString } = getMetricValueFormat(metric);
            const tooltipValues = {
                prefix: valuePrefix,
                suffix: valueSuffix,
                precision: valueDecimals,
                displayName: metric.display_name,
            };

            const axisOptions = {
                labelsFormat: formatString,
                title: metric.display_name,
            };

            switch (metric.name) {
                case xMetricName:
                    setXAxisOptions(axisOptions);
                    setTooltipFormat(prevState => ({ ...prevState, x: tooltipValues }));
                    break;
                case yMetricName:
                    setYAxisOptions(axisOptions);
                    setTooltipFormat(prevState => ({ ...prevState, y: tooltipValues }));
                    break;
                case zMetricName:
                    setTooltipFormat(prevState => ({ ...prevState, z: tooltipValues }));
                    break;
                default:
                    break;
            }
        });
    };

    useEffect(() => {
        // check which dimensions have data to se dimension selection in widget
        const availableDimensions = [];
        if (rowData.length && settings?.metrics) {
            const { xMetricName, yMetricName, zMetricName } = settings;
            getMetricName(widget.query.dimensions).forEach(dimension => {
                for (let i = 0; i < rowData.length; i++) {
                    const row = rowData[i];
                    const x = getMetricValue(xMetricName, row);
                    const y = getMetricValue(yMetricName, row);
                    const z = getMetricValue(zMetricName, row);
                    const name = getMetricValue(dimension, row);

                    if (
                        x &&
                        x !== NO_VALUE &&
                        y &&
                        y !== NO_VALUE &&
                        z &&
                        z !== NO_VALUE &&
                        name &&
                        name !== NO_VALUE
                    ) {
                        availableDimensions.push({
                            name: dimension,
                            displayName: getColumnDisplayName(reportColumns, dimension),
                        });
                        break;
                    }
                }
            });
            setDimensionsWithValues(availableDimensions);
        }
    }, [settings, rowData]);

    useEffect(() => {
        if (!dimensionsWithValues.length) {
            setIsDataParsed(false);
            return;
        }
        const { xMetricName, yMetricName, zMetricName } = settings;
        const filteredRowDataToUpdate = [];

        setAxisAndTooltipSettings(settings.metrics, xMetricName, yMetricName, zMetricName);

        const points = [];
        rowData.forEach(row => {
            const x = getMetricValue(xMetricName, row);
            const y = getMetricValue(yMetricName, row);
            const z = getMetricValue(zMetricName, row);
            const name = getMetricValue(selectedDimension.name, row);

            if (x && x !== NO_VALUE && y && y !== NO_VALUE && z && z !== NO_VALUE && name && name !== NO_VALUE) {
                filteredRowDataToUpdate.push(row);
                points.push(FORMAT_POINT_BY_WIDGET_TYPE[widget.type](x, y, z, name));
            }
        });
        if (widget.type === THREE_AXIS_WIDGETS_TYPES.Variwide) {
            points.sort((a, b) => b[1] - a[1]);
        }
        setSeries(points);
        setIsDataParsed(!!points.length);
    }, [settings, rowData, selectedDimension, dimensionsWithValues]);

    const topXSeries = series.slice(0, topXCount);
    const chartRef = useRef(null);

    const handleExportClick = () => {
        if (!chartRef.current?.chart) {
            return;
        }

        const chart = chartRef.current.chart;
        const widgetTitle = widget.name;
        const filename = widgetTitle || 'chart';

        const exportOptions = {
            filename,
        };

        const overriddenChartOptions = {
            title: { text: widgetTitle },
            // Enable data labels only for the exported image so values are visible
            plotOptions: {
                series: {
                    dataLabels: {
                        enabled: true,
                        formatter() {
                            const value = this.y;

                            if (typeof value !== 'number') {
                                return '';
                            }

                            // Keep a reasonable number of decimals for bubble/variwide charts
                            return value.toFixed(2);
                        },
                    },
                },
            },
        };

        const exportFn = chart.exportChartLocal ?? chart.exportChart;
        if (typeof exportFn === 'function') {
            exportFn.call(chart, exportOptions, overriddenChartOptions);
        }
    };
    const getChartByType = () =>
        widget.type === THREE_AXIS_WIDGETS_TYPES.Variwide ? (
            <VariwideChart
                tooltipFormat={tooltipFormat}
                points={topXSeries}
                height={height}
                width={width}
                chartRef={chartRef}
            />
        ) : (
            <BubbleChart
                yAxisOptions={yAxisOptions}
                xAxisOptions={xAxisOptions}
                tooltipFormat={tooltipFormat}
                points={topXSeries}
                height={height}
                width={width}
                chartRef={chartRef}
            />
        );

    return (
        <ChartWidgetWrapper
            apiFields={apiFields}
            widgetTitle={widget.name}
            widgetType={widget.type}
            widgetDataType={widget.dataType}
            widgetFilters={widget.query.filters}
            showSpinner={loading}
            showNoData={!loading && !error && !isDataParsed}
            blockedFilters={getBlockedWidgetFilterNames(widget.dataType, globalFilters)}
            showError={!loading && !!error}
            allowedActions={allowedActions}
            withTopX={withTopX}
            onExportClick={handleExportClick}
            readOnly={readOnly}
        >
            {!loading && !error && isDataParsed && (
                <>
                    {withTopX && (
                        <TopXChartDataFilter
                            topXCount={topXCount}
                            rowDataLength={series.length}
                            onChange={setTopXCount}
                        />
                    )}
                    {dimensionsWithValues.length > 1 && (
                        <DimensionsChartDataFilter
                            dimension={selectedDimension}
                            dimensions={dimensionsWithValues}
                            widgetName={widget.name}
                            onChange={setSelectedDimension}
                        />
                    )}
                    {getChartByType()}
                </>
            )}
        </ChartWidgetWrapper>
    );
};

ThreeAxisChartsWidget.propTypes = {
    dispatchWidgetVersions: PropTypes.func.isRequired,
    dispatchWidgetsLoading: PropTypes.func.isRequired,
    dashboardId: PropTypes.string.isRequired,
    widget: PropTypes.shape({
        name: PropTypes.string.isRequired,
        dataType: PropTypes.string.isRequired,
        type: PropTypes.oneOf(Object.values(THREE_AXIS_WIDGETS_TYPES)).isRequired,
        query: PropTypes.shape({
            dimensions: PropTypes.arrayOf(PropTypes.string),
            metrics: PropTypes.arrayOf(PropTypes.string),
            filters: PropTypes.arrayOf(
                PropTypes.shape({
                    field: PropTypes.string.isRequired,
                    operator: PropTypes.oneOf(OPERATOR_TYPES.map(x => x.name)).isRequired,
                    values: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
                })
            ),
        }).isRequired,
        options: PropTypes.shape({
            rows: PropTypes.arrayOf(PropTypes.string),
            columns: PropTypes.arrayOf(PropTypes.string),
            zAxis: PropTypes.arrayOf(PropTypes.string),
        }).isRequired,
        metricForTopX: PropTypes.string,
    }).isRequired,
    globalFilters: PropTypes.arrayOf(
        PropTypes.shape({
            field: PropTypes.string.isRequired,
            operator: PropTypes.oneOf(OPERATOR_TYPES.map(x => x.name)).isRequired,
            values: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
        })
    ),
    startDate: PropTypes.string.isRequired,
    endDate: PropTypes.string.isRequired,
    startDate2: PropTypes.string,
    endDate2: PropTypes.string,
    compare: PropTypes.bool.isRequired,
    height: PropTypes.number.isRequired,
    width: PropTypes.number.isRequired,
    updatingGraphQL: PropTypes.bool,
    allowedActions: PropTypes.arrayOf(PropTypes.oneOf(Object.values(ACTION_TYPES))),
    displayUnenriched: PropTypes.bool,
    onMenuClick: PropTypes.func,
    onCloneClick: PropTypes.func,
    onDeleteClick: PropTypes.func,
    apiFields: PropTypes.objectOf(PropTypes.any),
    readOnly: PropTypes.bool,
};

export default ThreeAxisChartsWidget;
